import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Alert } from "@repo/ui";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../../../redux/store";
import AnimatedCard from "../../../../components/AnimatedCard/AnimatedCard";
import PageContainer from "../../../../components/PageContainer/PageContainer";
import StepIndicator from "../../../../components/StepIndicator/StepIndicator";
import { registrationSteps } from "../../constants/steps";
import withAuth from "../../../../hoc/withAuth/withAuth";
import { withTranslations } from "../../../../hoc/withTranslations/withTranslations";
import { updateGiftTableState } from "../../../../redux/slices/giftTableSlice";
import { gsap } from "gsap";
import NavigationButtons from "../../components/NavigationButtons";

const LOCALE =
  "modules_Registration_pages_CreateGiftTable_CreateGiftTableStep1";

const CreateGiftTableStep1: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const giftTable = useSelector((state: RootState) => state.giftTable);
  const [eventType, setEventType] = useState(giftTable.eventType || "");
  const [alert, setAlert] = useState<{
    message: React.ReactNode;
    level: "info" | "warn" | "error" | "success";
  } | null>(null);

  const alertRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (alertRef.current) {
      gsap.fromTo(
        alertRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 0.5 }
      );
    }
  }, [alert]);

  useEffect(() => {
    if (giftTable.eventType) {
      setEventType(giftTable.eventType);
    }
  }, [giftTable.eventType]);

  const handleContinue = () => {
    if (!eventType) {
      setAlert({ message: t("eventTypeError"), level: "warn" });
      return;
    }
    dispatch(updateGiftTableState({ eventType }));
    navigate("/registration/create-gift-table/step2");
  };

  const handleBack = () => {
    navigate("/registration/accept-terms");
  };

  return (
    <PageContainer>
      <StepIndicator
        steps={registrationSteps}
        currentStep={2}
        progressPercentage={10}
      />
      <AnimatedCard title={t("title")} className="bg-white max-w-lg w-full">
        {alert && (
          <div ref={alertRef}>
            <Alert message={alert.message} level={alert.level} />
          </div>
        )}
        <div className="flex flex-col space-y-8">
          <p className="text-center">{t("kindMessage")}</p>
          <label htmlFor="eventType" className="text-label font-paragraph">
            {t("eventTypeLabel")}
          </label>
          <div className="flex flex-wrap justify-center gap-4">
            {[
              { key: "wedding", label: "eventTypeBoda" },
              { key: "quinceanera", label: "eventTypeXVAnos" },
              { key: "birthday", label: "eventTypeCumpleanos" },
              { key: "baptism", label: "eventTypeBautizo" },
              { key: "farewell", label: "eventTypeDespedida" },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setEventType(key)}
                className={`inline-flex h-10 items-center justify-center rounded-full px-4 ${eventType === key ? "bg-primary text-white" : "bg-gray-200"}`}
              >
                {t(label)}
              </button>
            ))}
          </div>
          <NavigationButtons
            continueLabel={t("continueButton")}
            backLabel={t("backButton", { defaultValue: "Regresar" })}
            onContinue={handleContinue}
            onBack={handleBack}
            className="w-full mt-4"
          />
        </div>
      </AnimatedCard>
    </PageContainer>
  );
};

export default withAuth(withTranslations(LOCALE)(CreateGiftTableStep1));
